import {
  Alert,
  Box,
  Button,
  Header,
  Link,
  Pagination,
  Select,
  SpaceBetween,
  Table,
  TextFilter,
} from '@cloudscape-design/components';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';

import { listVoiceSalesCampaigns } from '../../common/api/voiceSales';
import { formatDateStringWithTime } from '../../common/formatting/dateFormat';
import { DashboardViewLayout } from '../../components/DashboardViewLayout';
import {
  filterAndSortCampaigns,
  paginateItems,
  type CampaignSortingField,
  type CampaignsCollectionState,
  type SortDirection,
} from './collections';
import { VoiceSalesStatus } from './presentation';

export type CampaignsSearch = {
  q?: string;
  status?: string;
  mode?: string;
  sortingField?: CampaignSortingField;
  sortDirection?: SortDirection;
  page?: number;
};

const PAGE_SIZE = 20;
const STATUS_OPTIONS = [
  { label: 'All statuses', value: '' },
  { label: 'Draft', value: 'draft' },
  { label: 'Scheduled', value: 'scheduled' },
  { label: 'In progress', value: 'running' },
  { label: 'Completed', value: 'completed' },
  { label: 'Needs attention', value: 'failed' },
];
const MODE_OPTIONS = [
  { label: 'All calling modes', value: '' },
  { label: 'Preview only', value: 'dry_run' },
  { label: 'Approved customer calling', value: 'real' },
];
const SORT_OPTIONS: Array<{ label: string; value: CampaignSortingField }> = [
  { label: 'Campaign', value: 'campaign' },
  { label: 'Status', value: 'status' },
  { label: 'Daily limit', value: 'dailyLimit' },
  { label: 'Created', value: 'created' },
];

function statusCode(error: unknown): number {
  return typeof error === 'object' && error !== null && 'status' in error
    ? Number((error as { status?: unknown }).status ?? 0)
    : 0;
}

function callingMode(mode: string): string {
  return mode === 'real' ? 'Approved customer calling' : 'Preview only';
}

function LabelledValue({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <Box variant="awsui-key-label">{label}</Box>
      <Box variant="span">{children}</Box>
    </div>
  );
}

export default function VoiceSalesCampaignsPage() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as CampaignsSearch;
  const state: CampaignsCollectionState = {
    q: search.q ?? '',
    status: search.status ?? '',
    mode: search.mode ?? '',
    sortingField: search.sortingField ?? 'created',
    sortDirection: search.sortDirection ?? 'desc',
  };
  const page = Math.max(1, Number(search.page) || 1);
  const campaignsQuery = useQuery({
    queryKey: ['voice-sales-campaigns'],
    queryFn: () => listVoiceSalesCampaigns(),
  });
  const filtered = filterAndSortCampaigns(campaignsQuery.data ?? [], state);
  const paged = paginateItems(filtered, page, PAGE_SIZE);
  const denied = statusCode(campaignsQuery.error) === 403;

  const updateSearch = (updates: Partial<CampaignsSearch>) => {
    void navigate({ to: '/dashboard/voice-sales/campaigns', search: { ...search, ...updates } });
  };

  return (
    <DashboardViewLayout
      breadcrumbs={[
        { text: 'Voice Sales', href: '/dashboard/voice-sales' },
        { text: 'Campaigns', href: '/dashboard/voice-sales/campaigns' },
      ]}
      contentType="table"
    >
      <Table
        variant="full-page"
        stickyHeader
        stripedRows
        resizableColumns
        wrapLines
        loading={campaignsQuery.isPending}
        loadingText="Loading voice sales campaigns"
        items={denied ? [] : paged.items}
        sortingColumn={{ sortingField: state.sortingField }}
        sortingDescending={state.sortDirection === 'desc'}
        onSortingChange={({ detail }) =>
          updateSearch({
            sortingField: (detail.sortingColumn.sortingField ?? 'created') as CampaignSortingField,
            sortDirection: detail.isDescending ? 'desc' : 'asc',
            page: 1,
          })
        }
        pagination={
          <Pagination
            currentPageIndex={paged.currentPage}
            pagesCount={paged.pagesCount}
            disabled={campaignsQuery.isPending}
            onChange={({ detail }) => updateSearch({ page: detail.currentPageIndex })}
          />
        }
        filter={
          <SpaceBetween direction="horizontal" size="xs">
            <TextFilter
              filteringText={state.q}
              filteringPlaceholder="Search campaigns"
              filteringAriaLabel="Search campaigns"
              countText={`${filtered.length} loaded result${filtered.length === 1 ? '' : 's'}`}
              onChange={({ detail }) => updateSearch({ q: detail.filteringText || undefined, page: 1 })}
            />
            <Select
              ariaLabel="Status"
              selectedOption={STATUS_OPTIONS.find((option) => option.value === state.status) ?? STATUS_OPTIONS[0]}
              options={STATUS_OPTIONS}
              onChange={({ detail }) => updateSearch({ status: detail.selectedOption.value || undefined, page: 1 })}
            />
            <Select
              ariaLabel="Calling mode"
              selectedOption={MODE_OPTIONS.find((option) => option.value === state.mode) ?? MODE_OPTIONS[0]}
              options={MODE_OPTIONS}
              onChange={({ detail }) => updateSearch({ mode: detail.selectedOption.value || undefined, page: 1 })}
            />
            <Select
              ariaLabel="Sort campaigns by"
              selectedOption={SORT_OPTIONS.find((option) => option.value === state.sortingField) ?? SORT_OPTIONS[0]}
              options={SORT_OPTIONS}
              onChange={({ detail }) =>
                updateSearch({ sortingField: detail.selectedOption.value as CampaignSortingField, page: 1 })
              }
            />
          </SpaceBetween>
        }
        empty={
          <Box color="inherit" margin={{ vertical: 'xs' }} textAlign="center">
            {denied ? (
              <Alert type="error" header="You do not have access to Voice Sales.">
                Ask an internal administrator if you believe you need access.
              </Alert>
            ) : campaignsQuery.error ? (
              <SpaceBetween size="s">
                <Box fontWeight="bold">Campaigns could not be loaded</Box>
                <Button onClick={() => campaignsQuery.refetch()}>Retry</Button>
              </SpaceBetween>
            ) : (
              <SpaceBetween size="xxs">
                <Box fontWeight="bold">No voice sales campaigns match these filters.</Box>
                <Box variant="p" color="text-body-secondary">Clear search or filters to review all loaded campaigns.</Box>
              </SpaceBetween>
            )}
          </Box>
        }
        header={
          <Header
            variant="awsui-h1-sticky"
            counter={`(${filtered.length})`}
            description="Search and sort the latest 100 loaded campaigns."
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button iconName="refresh" loading={campaignsQuery.isFetching} onClick={() => campaignsQuery.refetch()}>
                  Refresh
                </Button>
                <Button variant="primary" onClick={() => navigate({ to: '/dashboard/voice-sales/campaigns/new' })}>
                  Create campaign
                </Button>
              </SpaceBetween>
            }
          >
            Campaigns
          </Header>
        }
        columnDefinitions={[
          {
            id: 'campaign',
            header: 'Campaign',
            cell: (campaign) => (
              <SpaceBetween direction="vertical" size="xxxs">
                <Header variant="h3">
                  <Link
                    href={`/dashboard/voice-sales/campaigns/${campaign.uuid}`}
                    onFollow={(event) => {
                      event.preventDefault();
                      void navigate({
                        to: '/dashboard/voice-sales/campaigns/$campaignUuid',
                        params: { campaignUuid: campaign.uuid },
                      });
                    }}
                  >
                    {campaign.name}
                  </Link>
                </Header>
                <VoiceSalesStatus status={campaign.status} />
                <Box variant="span">{callingMode(campaign.mode)}</Box>
              </SpaceBetween>
            ),
            sortingField: 'campaign',
            minWidth: 230,
          },
          {
            id: 'audienceRegion',
            header: 'Audience & region',
            cell: (campaign) => (
              <SpaceBetween direction="vertical" size="xxxs">
                <LabelledValue label="Audience">Dallas contact discovery</LabelledValue>
                <LabelledValue label="Region">{campaign.source_region}</LabelledValue>
              </SpaceBetween>
            ),
            minWidth: 190,
          },
          {
            id: 'scheduling',
            header: 'Scheduling',
            cell: (campaign) => (
              <SpaceBetween direction="vertical" size="xxxs">
                <LabelledValue label="Daily limit">{campaign.daily_cap} per day</LabelledValue>
                <LabelledValue label="Timezone">{campaign.timezone}</LabelledValue>
              </SpaceBetween>
            ),
            sortingField: 'dailyLimit',
            minWidth: 170,
          },
          {
            id: 'activity',
            header: 'Activity',
            cell: (campaign) => (
              <SpaceBetween direction="vertical" size="xxxs">
                <LabelledValue label="Created">{formatDateStringWithTime(campaign.created_at)}</LabelledValue>
                <LabelledValue label="Updated">{formatDateStringWithTime(campaign.updated_at)}</LabelledValue>
              </SpaceBetween>
            ),
            sortingField: 'created',
            minWidth: 210,
          },
        ]}
      />
    </DashboardViewLayout>
  );
}
